import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { ExternalLinkIcon, ArrowUpDownIcon, SearchIcon, FilterIcon } from 'lucide-react';
const mockPoliciesData = [{
  id: 'P001',
  scraperId: '001',
  scraperName: 'Aetna',
  policyName: 'Continuous Glucose Monitoring',
  policyCode: 'CGM-2023',
  sourceUrl: 'https://www.aetna.com/cpb/medical/data/1_99/0070.html',
  ageInDays: 2,
  lastUpdated: '2023-06-19'
}, {
  id: 'P002',
  scraperId: '039',
  scraperName: 'AlohaCare',
  policyName: 'Genetic Testing',
  policyCode: 'GT-2023-01',
  sourceUrl: 'https://www.alohacare.org/providers/medical-policies/genetic-testing/',
  ageInDays: 5,
  lastUpdated: '2023-06-16'
}, {
  id: 'P003',
  scraperId: '027',
  scraperName: 'AmeriHealth',
  policyName: 'Bariatric Surgery',
  policyCode: 'BS-2023-05',
  sourceUrl: 'https://www.amerihealth.com/providers/medical_policies/surgical/bariatric.html',
  ageInDays: 1,
  lastUpdated: '2023-06-20'
}, {
  id: 'P004',
  scraperId: '004',
  scraperName: 'Arkansas BCBS',
  policyName: 'Proton Beam Therapy',
  policyCode: 'PBT-2023-02',
  sourceUrl: 'https://www.arkansasbluecross.com/providers/medical-policies/proton-beam',
  ageInDays: 7,
  lastUpdated: '2023-06-14'
}, {
  id: 'P005',
  scraperId: '040',
  scraperName: 'AvMed',
  policyName: 'Transcatheter Aortic Valve',
  policyCode: 'TAV-2023-01',
  sourceUrl: 'https://www.avmed.org/providers/policies/tavr',
  ageInDays: 3,
  lastUpdated: '2023-06-18'
}, {
  id: 'P006',
  scraperId: '052',
  scraperName: 'BCBS Wellmark',
  policyName: 'CAR-T Cell Therapy',
  policyCode: 'CART-2023-01',
  sourceUrl: 'https://www.wellmark.com/Provider/MedPoliciesAndAuthorizations/cart-cell.aspx',
  ageInDays: 4,
  lastUpdated: '2023-06-17'
}, {
  id: 'P007',
  scraperId: '041',
  scraperName: 'Baylor Scott',
  policyName: 'Spinal Cord Stimulation',
  policyCode: 'SCS-2023-03',
  sourceUrl: 'https://www.bswhealth.com/providers/policies-guidelines/spinal-cord-stim',
  ageInDays: 10,
  lastUpdated: '2023-06-11'
}, {
  id: 'P008',
  scraperId: '013',
  scraperName: 'BCBS Mississippi',
  policyName: 'Artificial Disc Replacement',
  policyCode: 'ADR-2023-02',
  sourceUrl: 'https://www.bcbsms.com/providers/medical-policy-coverage-guidelines/disc-replacement',
  ageInDays: 8,
  lastUpdated: '2023-06-13'
}, {
  id: 'P009',
  scraperId: '029',
  scraperName: 'BCBS Rhode Island',
  policyName: 'Breast Reconstruction',
  policyCode: 'BR-2023-04',
  sourceUrl: 'https://www.bcbsri.com/providers/policies/breast-reconstruction',
  ageInDays: 12,
  lastUpdated: '2023-06-09'
}, {
  id: 'P010',
  scraperId: '003',
  scraperName: 'BCBS Arizona',
  policyName: 'Stem Cell Transplant',
  policyCode: 'SCT-2023-01',
  sourceUrl: 'https://www.azblue.com/healthcareprofessionals/resource-center/policies/stem-cell',
  ageInDays: 6,
  lastUpdated: '2023-06-15'
}];
const Policies: React.FC = () => {
  const {
    theme
  } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const filteredData = mockPoliciesData.filter(policy => policy.id.toLowerCase().includes(searchTerm.toLowerCase()) || policy.policyName.toLowerCase().includes(searchTerm.toLowerCase()) || policy.scraperName.toLowerCase().includes(searchTerm.toLowerCase()) || policy.scraperId.toLowerCase().includes(searchTerm.toLowerCase()));
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    const fieldA = a[sortField as keyof typeof a];
    const fieldB = b[sortField as keyof typeof b];
    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return <div className={`p-6 min-h-full ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`} data-id="element-355">
      <div className="mb-6" data-id="element-356">
        <h2 className="text-2xl font-extralight tracking-tight mb-2" data-id="element-357">
          Policy Database
        </h2>
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-extralight`} data-id="element-358">
          Track and manage all policy documents across payers
        </p>
      </div>
      <div className={`p-4 rounded-lg mb-6 ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border border-gray-200'}`} data-id="element-359">
        <div className="flex items-center mb-4" data-id="element-360">
          <div className="relative flex-1" data-id="element-361">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-362">
              <SearchIcon size={16} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} data-id="element-363" />
            </div>
            <input type="text" placeholder="Search policies..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`pl-10 pr-4 py-2 w-full rounded-md text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border focus:outline-none focus:ring-2 ${theme === 'dark' ? 'focus:ring-fuchsia-500' : 'focus:ring-fuchsia-300'}`} data-id="element-364" />
          </div>
          <button className={`ml-2 p-2 rounded-md ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} title="Filter options" data-id="element-365">
            <FilterIcon size={18} data-id="element-366" />
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-367">
          <table className="w-full" data-id="element-368">
            <thead className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'} data-id="element-369">
              <tr data-id="element-370">
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('id')} data-id="element-371">
                  <div className="flex items-center" data-id="element-372">
                    Policy ID
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-373" />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('scraperId')} data-id="element-374">
                  <div className="flex items-center" data-id="element-375">
                    Scraper ID
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-376" />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('scraperName')} data-id="element-377">
                  <div className="flex items-center" data-id="element-378">
                    Payer
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-379" />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('policyName')} data-id="element-380">
                  <div className="flex items-center" data-id="element-381">
                    Policy Name
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-382" />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-383">
                  Policy Code
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium" data-id="element-384">
                  Source URL
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('ageInDays')} data-id="element-385">
                  <div className="flex items-center" data-id="element-386">
                    Age (Days)
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-387" />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium cursor-pointer" onClick={() => handleSort('lastUpdated')} data-id="element-388">
                  <div className="flex items-center" data-id="element-389">
                    Last Updated
                    <ArrowUpDownIcon size={14} className="ml-1" data-id="element-390" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30" data-id="element-391">
              {sortedData.map(policy => <tr key={policy.id} className={`${theme === 'dark' ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`} data-id="element-392">
                  <td className="px-4 py-3 text-sm" data-id="element-393">{policy.id}</td>
                  <td className="px-4 py-3 text-sm" data-id="element-394">{policy.scraperId}</td>
                  <td className="px-4 py-3 text-sm" data-id="element-395">{policy.scraperName}</td>
                  <td className="px-4 py-3 text-sm" data-id="element-396">{policy.policyName}</td>
                  <td className="px-4 py-3 text-sm" data-id="element-397">{policy.policyCode}</td>
                  <td className="px-4 py-3 text-sm" data-id="element-398">
                    <div className="flex items-center" data-id="element-399">
                      <a href={policy.sourceUrl} target="_blank" rel="noopener noreferrer" className={`truncate max-w-xs ${theme === 'dark' ? 'text-fuchsia-400 hover:text-fuchsia-300' : 'text-fuchsia-600 hover:text-fuchsia-800'}`} data-id="element-400">
                        {policy.sourceUrl}
                      </a>
                      <ExternalLinkIcon size={14} className="ml-1 flex-shrink-0" data-id="element-401" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm" data-id="element-402">
                    <span className={policy.ageInDays > 7 ? 'text-yellow-500' : 'text-green-500'} data-id="element-403">
                      {policy.ageInDays}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm" data-id="element-404">{policy.lastUpdated}</td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
export default Policies;